import type { ChangeEvent } from "react";

// Helper function to generate a realistic short URL preview
function generateShortUrlPreview(url: string): string {
  // For preview purposes, simulate the short URL format
  // In real implementation, this would call the actual URL shortening service
  return "https://activqms.com/t/abc123";
}

export interface CharacterLimitInputProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  destinationLinkLength?: number;
  showWarningAt?: number;
  placeholder?: string;
  label?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  destinationUrl?: string; // Optional actual destination URL for more accurate preview
}

export function CharacterLimitInput({
  value,
  onChange,
  maxLength = 130, // Auto-responder limit to leave room for destination link
  destinationLinkLength = 25, // Estimated average link length
  showWarningAt = 0.9,
  placeholder,
  label,
  rows = 4,
  className = "",
  disabled = false,
  destinationUrl,
}: CharacterLimitInputProps) {
  const currentLength = value.length;
  const warningThreshold = Math.floor(maxLength * showWarningAt);
  const totalSMSLimit = 160;

  // Calculate actual or estimated link length
  const actualLinkLength = destinationUrl
    ? generateShortUrlPreview(destinationUrl).length
    : destinationLinkLength;

  const combinedLength = currentLength + 1 + actualLinkLength; // +1 for space

  // Generate truncated message if it would exceed SMS limit
  const getTruncatedMessage = () => {
    if (combinedLength <= totalSMSLimit) {
      return value;
    }

    // Calculate how much space we have for the message
    const availableSpace = totalSMSLimit - 1 - actualLinkLength - 3; // -3 for "..."

    if (availableSpace <= 0) {
      return ""; // Link is too long, no space for message
    }

    return value.substring(0, availableSpace) + "...";
  };

  const truncatedMessage = getTruncatedMessage();
  const willBeTruncated = combinedLength > totalSMSLimit && value.length > 0;

  // Determine color based on character count
  const getCharacterCountColor = () => {
    if (currentLength > maxLength) return "text-red-500";
    if (currentLength >= warningThreshold) return "text-orange-500";
    return "text-gray-500";
  };

  // Determine border color based on character count
  const getBorderColor = () => {
    if (currentLength > maxLength)
      return "border-red-300 focus:border-red-500 focus:ring-red-500";
    if (currentLength >= warningThreshold)
      return "border-orange-300 focus:border-orange-500 focus:ring-orange-500";
    return "border-gray-300 focus:border-blue-500 focus:ring-blue-500";
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      <div className="relative">
        <textarea
          value={value}
          onChange={handleChange}
          className={`w-full border rounded-lg px-3 py-2 focus:ring-2 resize-none ${getBorderColor()}`}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
        />

        {/* Character count indicator */}
        <div
          className={`absolute bottom-2 right-2 text-xs ${getCharacterCountColor()}`}
          data-testid="character-count"
        >
          <span
            data-testid="character-count-indicator"
            className={getCharacterCountColor()}
          >
            {currentLength}
          </span>
          /<span data-testid="character-limit">{maxLength}</span>
        </div>
      </div>

      {/* Warning and error messages */}
      <div className="mt-1 space-y-1">
        {currentLength > maxLength && (
          <p className="text-sm text-red-600" data-testid="character-error">
            Message exceeds {maxLength} character limit for auto-responders.
          </p>
        )}

        {currentLength >= warningThreshold && currentLength <= maxLength && (
          <p
            className="text-sm text-orange-600"
            data-testid="character-warning"
          >
            Approaching character limit ({currentLength}/{maxLength}{" "}
            characters).
          </p>
        )}

        {/* Combined message preview */}
        {currentLength > 0 && (
          <div className="text-xs text-gray-500 space-y-1">
            <p>
              Combined with link: {combinedLength}/{totalSMSLimit} characters
              {combinedLength > totalSMSLimit && (
                <span className="text-red-500 ml-1">(exceeds SMS limit)</span>
              )}
            </p>

            {/* Show truncation warning */}
            {willBeTruncated && (
              <p className="text-orange-600 font-medium">
                ⚠️ Message will be truncated to fit SMS limit
              </p>
            )}

            {/* Preview of actual message that will be sent */}
            <div className="bg-gray-50 border rounded p-2 mt-2">
              <p className="text-xs text-gray-600 mb-1">
                {willBeTruncated ? "SMS Preview (truncated):" : "SMS Preview:"}
              </p>
              <p className="italic">
                "{truncatedMessage}{" "}
                {destinationUrl
                  ? generateShortUrlPreview(destinationUrl)
                  : "https://activqms.com/t/abc123"}
                "
              </p>
              {willBeTruncated && (
                <p className="text-xs text-orange-600 mt-1">
                  Original message was {currentLength} characters, truncated to{" "}
                  {truncatedMessage.length} characters
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
